import Input from '@/components/ui/forms/input';
import Select from '@/components/ui/select/select';
import type { DeliveryPartnerBank } from '@/types/delivery-partner';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import {
  ACCOUNT_TYPE_OPTIONS,
  MODALIDADE_OPTIONS,
  PIX_KEY_TYPE_OPTIONS,
} from '../constants';
import { ChoiceGroup, Fieldset } from '../fields';
import { maskCnpj, maskCpf, maskPhone } from '../masks';
import type { DeliveryPartnerFormValues } from '../schema';

const YES_NO_OPTIONS = [
  { value: 'true', label: 'Sim' },
  { value: 'false', label: 'Não' },
];

const PIX_PLACEHOLDERS: Record<string, string> = {
  CPF: '000.000.000-00',
  PHONE: '(11) 99999-9999',
  EMAIL: 'voce@email.com',
  RANDOM: '00000000-0000-0000-0000-000000000000',
};

interface Props {
  banks: DeliveryPartnerBank[];
}

export default function StepDadosProfissionais({ banks }: Props) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<DeliveryPartnerFormValues>();

  const modalidade = watch('modalidade');
  const hasPix = watch('has_pix');
  const pixKeyType = watch('pix_key_type');
  const bankName = watch('bank_name');

  const bankOptions = useMemo(
    () =>
      banks.map((bank) => ({
        value: bank.name,
        label: bank.code === '000' ? bank.name : `${bank.code} — ${bank.name}`,
        code: bank.code,
      })),
    [banks],
  );

  const selectedBank =
    bankOptions.find((option) => option.value === bankName) ?? null;

  return (
    <>
      <Fieldset
        legend="Modalidade de prestação de serviço"
        description="Escolha como você quer prestar serviço para a Qualidoc."
      >
        <div className="space-y-6">
          <Controller
            name="modalidade"
            control={control}
            render={({ field }) => (
              <ChoiceGroup
                label="Modalidade"
                name="modalidade"
                options={MODALIDADE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={errors.modalidade?.message}
              />
            )}
          />

          {modalidade === 'MEI' && (
            <Controller
              name="cnpj"
              control={control}
              render={({ field }) => (
                <Input
                  label="CNPJ do MEI"
                  required
                  inputMode="numeric"
                  placeholder="00.000.000/0000-00"
                  className="sm:max-w-sm"
                  name={field.name}
                  ref={field.ref}
                  value={field.value ?? ''}
                  onBlur={field.onBlur}
                  onChange={(event) =>
                    field.onChange(maskCnpj(event.target.value))
                  }
                  error={errors.cnpj?.message}
                />
              )}
            />
          )}

          <Controller
            name="has_smartphone"
            control={control}
            render={({ field }) => (
              <ChoiceGroup
                label="Você tem smartphone com acesso à internet?"
                name="has_smartphone"
                options={YES_NO_OPTIONS}
                value={toRadioValue(field.value)}
                onChange={(value) => field.onChange(value === 'true')}
                error={errors.has_smartphone?.message}
              />
            )}
          />

          <Controller
            name="has_app"
            control={control}
            render={({ field }) => (
              <ChoiceGroup
                label="Você já baixou o OnHere ou o AgileDelivery?"
                name="has_app"
                options={YES_NO_OPTIONS}
                value={toRadioValue(field.value)}
                onChange={(value) => field.onChange(value === 'true')}
                error={errors.has_app?.message}
              />
            )}
          />

          <Controller
            name="accepts_contact"
            control={control}
            render={({ field }) => (
              <ChoiceGroup
                label="Autoriza contato por telefone, WhatsApp ou SMS?"
                name="accepts_contact"
                options={YES_NO_OPTIONS}
                value={toRadioValue(field.value)}
                onChange={(value) => field.onChange(value === 'true')}
                error={errors.accepts_contact?.message}
              />
            )}
          />
        </div>
      </Fieldset>

      <Fieldset
        legend="Dados bancários"
        description="A conta precisa estar no seu nome ou no CNPJ do seu MEI. Guardamos estes dados criptografados."
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor="bank_name"
              className="mb-3 block text-sm font-semibold leading-none text-gray-200"
            >
              Banco *
            </label>
            <Select
              inputId="bank_name"
              options={bankOptions}
              value={selectedBank}
              placeholder="Busque pelo nome ou código do banco"
              noOptionsMessage={() => 'Nenhum banco encontrado'}
              onChange={(option) => {
                const bank = option as (typeof bankOptions)[number] | null;
                setValue('bank_name', bank?.value ?? '', {
                  shouldValidate: true,
                });
                setValue('bank_code', bank?.code ?? '');
              }}
            />
            {errors.bank_name?.message && (
              <p className="mt-2 text-xs text-red-500">
                {errors.bank_name.message}
              </p>
            )}
          </div>

          <Controller
            name="account_type"
            control={control}
            render={({ field }) => (
              <ChoiceGroup
                label="Tipo de conta"
                name="account_type"
                options={ACCOUNT_TYPE_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={errors.account_type?.message}
              />
            )}
          />

          <div className="grid gap-5 sm:grid-cols-2">
            <Input
              label="Agência"
              required
              inputMode="numeric"
              placeholder="0001"
              {...register('bank_agency')}
              error={errors.bank_agency?.message}
            />
            <Input
              label="Conta com dígito"
              required
              placeholder="12345678-9"
              {...register('bank_account')}
              error={errors.bank_account?.message}
            />
          </div>

          <Controller
            name="has_pix"
            control={control}
            render={({ field }) => (
              <ChoiceGroup
                label="Você tem chave PIX?"
                name="has_pix"
                options={YES_NO_OPTIONS}
                value={toRadioValue(field.value)}
                onChange={(value) => field.onChange(value === 'true')}
                error={errors.has_pix?.message}
              />
            )}
          />

          {hasPix === true && (
            <div className="space-y-6">
              <Controller
                name="pix_key_type"
                control={control}
                render={({ field }) => (
                  <ChoiceGroup
                    label="Tipo de chave PIX"
                    name="pix_key_type"
                    options={PIX_KEY_TYPE_OPTIONS}
                    value={field.value}
                    onChange={(value) => {
                      field.onChange(value);
                      setValue('pix_key', '');
                    }}
                    error={errors.pix_key_type?.message}
                  />
                )}
              />

              <Controller
                name="pix_key"
                control={control}
                render={({ field }) => (
                  <Input
                    label="Chave PIX"
                    required
                    className="sm:max-w-md"
                    placeholder={
                      PIX_PLACEHOLDERS[pixKeyType ?? ''] ?? 'Sua chave PIX'
                    }
                    name={field.name}
                    ref={field.ref}
                    value={field.value ?? ''}
                    onBlur={field.onBlur}
                    onChange={(event) =>
                      field.onChange(maskPixKey(pixKeyType, event.target.value))
                    }
                    error={errors.pix_key?.message}
                  />
                )}
              />
            </div>
          )}
        </div>
      </Fieldset>
    </>
  );
}

function toRadioValue(value: boolean | undefined): string | undefined {
  return value === undefined ? undefined : String(value);
}

function maskPixKey(type: string | undefined, value: string): string {
  if (type === 'CPF') {
    return maskCpf(value);
  }
  if (type === 'PHONE') {
    return maskPhone(value);
  }
  return value;
}
